/**
 * Libellés de la fiche cours et de la confirmation v2. Fonctions pures (dates en heure locale du
 * site : Mindbody renvoie `StartDateTime` sans fuseau, comme le reste de l'app).
 */
import { differenceInCalendarDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';

const cap = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/*
 * Espaces insécables (revue du 04/10) : jour, mois et année, nombre et unité (« 1 crédit », « 12 h ») ne se séparent
 * jamais en fin de ligne (« 11 / mars 2027 », « (1 / crédit) »). U+00A0 littéral dans les motifs date-fns (caractère
 * hors a-z / A-Z : recopié tel quel).
 */
const NBSP = '\u00a0';

/** « Lundi 5 octobre » (maquette : date longue du bandeau, passée dans le panneau, SPEC FC1). */
export function longDate(date: Date): string {
  return cap(format(date, `EEEE d${NBSP}MMMM`, { locale: fr }));
}

/** « 07:45 » */
export function hour(date: Date): string {
  return format(date, 'HH:mm');
}

/** « 07:45 — 08:30 » */
export function timeRange(start: Date, end?: Date | null): string {
  return end && !Number.isNaN(end.getTime()) && end > start ? `${hour(start)} — ${hour(end)}` : hour(start);
}

/** « 5 octobre 2026 · 07:45 » (billet de confirmation). */
export function ticketDate(date: Date): string {
  return `${format(date, `d${NBSP}MMMM${NBSP}yyyy`, { locale: fr })} · ${hour(date)}`;
}

/** « 11 mars 2027 » (expiration d'un forfait). */
export function dayMonthYear(date: Date): string {
  return format(date, `d${NBSP}MMMM${NBSP}yyyy`, { locale: fr });
}

/** « 4 oct. » */
export function shortDate(date: Date): string {
  return format(date, `d${NBSP}MMM`, { locale: fr });
}

/** Durée en minutes entre début et fin (0 si illisible). */
export function durationMinutes(start: string, end: string | null | undefined): number {
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : NaN;
  return Number.isFinite(a) && Number.isFinite(b) && b > a ? Math.round((b - a) / 60000) : 0;
}

/**
 * Titre de la confirmation : « Rendez-vous aujourd'hui. », « … demain. », « … lundi. » dans la
 * semaine, sinon « … lundi 12 octobre. » (maquette : « Rendez-vous lundi. »).
 */
export function rendezVousLabel(start: Date, now: Date = new Date()): string {
  const days = differenceInCalendarDays(start, now);
  if (days <= 0) return "Rendez-vous aujourd’hui.";
  if (days === 1) return 'Rendez-vous demain.';
  if (days < 7) return `Rendez-vous ${format(start, 'EEEE', { locale: fr })}.`;
  return `Rendez-vous ${format(start, `EEEE d${NBSP}MMMM`, { locale: fr })}.`;
}

/**
 * Ouverture des réservations d'un cours (état « pas encore ouvert ») : « aujourd’hui à 12 h », « demain à 12 h », sinon
 * « le 5 oct. à 12 h » (revue du 05/10 : « le 5 oct. » le jour même). Heure pleine écrite « 12 h », sinon « 12:30 ».
 */
export function opensLabel(opens: Date, now: Date = new Date()): string {
  const time = hour(opens).replace(/:00$/, `${NBSP}h`);
  const days = differenceInCalendarDays(opens, now);
  if (days === 0) return `aujourd’hui à ${time}`;
  if (days === 1) return `demain à ${time}`;
  return `le ${shortDate(opens)} à ${time}`;
}

export function plural(n: number, one: string, many?: string): string {
  return `${n}${NBSP}${Math.abs(n) < 2 ? one : many ?? `${one}s`}`;
}

/**
 * Nom du studio sans le nom de la marque qui le préfixe dans Mindbody (« dynamo Sèvres » → « Sèvres »
 * sur Dynamo) : le kicker affiche déjà « MARQUE · STUDIO ».
 */
export function studioLabel(locationName: string | null | undefined, brandName?: string | null): string {
  const name = (locationName ?? '').trim();
  const brand = (brandName ?? '').trim();
  if (!brand || name.length <= brand.length) return name;
  if (name.toLowerCase().startsWith(`${brand.toLowerCase()} `)) return name.slice(brand.length).trim();
  return name;
}

export interface AddressSource {
  Address?: string | null;
  Address2?: string | null;
  City?: string | null;
  PostalCode?: string | null;
}

/** Adresse du studio en une ligne : `Address`, puis `Address2` ou « code postal ville ». */
export function addressLine(location: AddressSource | null | undefined): string {
  if (!location) return '';
  const first = (location.Address ?? '').trim();
  const second = (location.Address2 ?? '').trim() || [location.PostalCode, location.City].filter(Boolean).join(' ').trim();
  return [first, second].filter(Boolean).join(', ');
}

/**
 * « de » élidé devant une voyelle ou un h : « Les cours d’Estelle », « Les cours de Manon ».
 * Retour de Morgane (Punch Annecy, 22/10) sur « Les cours de Estelle ». Pas devant un « y » : « Les cours de yoga »
 * (recette de Ségolène, RIISE 28/10 : « les cours d'Yoga »), « de Yasmine ».
 */
export function deElide(word: string): string {
  const w = word.trim();
  return /^[aeiouhàâäéèêëîïôöùûüœæ]/i.test(w) ? `d’${w}` : `de ${w}`;
}

/** Prénom du coach (DisplayName Mindbody, sinon prénom), capitalisé comme en v1. */
export function coachName(staff: { DisplayName?: string | null; FirstName?: string | null } | null | undefined): string {
  const raw = (staff?.DisplayName || staff?.FirstName || '').trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
}

/** Cours ouvert aux packs étudiants : l'étoile est dans le nom Mindbody (`DYNAMO SIGNATURE*`). */
export function isStudentClass(name: string | null | undefined): boolean {
  return /\*\s*$/.test(name ?? '');
}

/* ------------------------------------------------------- annulation (FC9) */

/** Limite de l'annulation gratuite : début du cours moins la fenêtre du site. */
export function freeCancelDeadline(start: Date, lateCancelHours: number): Date {
  return new Date(start.getTime() - lateCancelHours * 3600000);
}

/** « Annulation gratuite jusqu'à 12 h avant le cours. » (fenêtre du site du cours). */
export function freeCancelHint(lateCancelHours: number): string {
  return `Annulation gratuite jusqu’à ${lateCancelHours}${NBSP}h avant le cours.`;
}

/**
 * Cours réservé : « Annulation gratuite jusqu'au 4 oct. à 19:45. », ou, passé ce délai, l'annonce de
 * l'annulation tardive (même règle que utils/functions.ts `isVisitLateCancel` : heures pleines).
 */
export function bookedCancelHint(start: Date, lateCancelHours: number, now: Date = new Date()): string {
  const deadline = freeCancelDeadline(start, lateCancelHours);
  if (now >= deadline) {
    return `Annulation tardive${NBSP}: à moins de ${lateCancelHours}${NBSP}h du cours, le crédit n’est pas recrédité.`;
  }
  return `Annulation gratuite jusqu’au ${shortDate(deadline)} à ${hour(deadline)}.`;
}

/**
 * Avertissement de la popin d'annulation tardive (Mon compte : VisitActions ; fiche cours : CancelModal). Recette de
 * Ségolène (RIISE Paris, 28/10), décision de Luc : le bouton reste « Annuler », l'avertissement est dans la popin.
 * Pas de nombre de crédits : la visite ne dit pas combien le cours en a débité.
 */
export function lateCancelWarning(lateCancelHours: number, two = false): { lead: string; text: string } {
  return {
    lead: `Attention${NBSP}: annulation tardive.`,
    text: `Vous êtes à moins de ${lateCancelHours}${NBSP}h du cours, ${
      two ? 'les deux places réservées seront perdues' : 'la séance réservée sera perdue'
    } (pas de recrédit).`,
  };
}

/** Texte nettoyé de la description Mindbody (HTML) : entités, balises, espaces. Sans DOM. */
export function plainDescription(html: string | null | undefined): string {
  if (!html) return '';
  const named: Record<string, string> = {
    nbsp: ' ',
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: '’',
    rsquo: '’',
    lsquo: '‘',
    rdquo: '”',
    ldquo: '“',
    hellip: '…',
    ndash: '–',
    mdash: '—',
    eacute: 'é',
    egrave: 'è',
    ecirc: 'ê',
    euml: 'ë',
    agrave: 'à',
    acirc: 'â',
    ccedil: 'ç',
    icirc: 'î',
    iuml: 'ï',
    ocirc: 'ô',
    ugrave: 'ù',
    ucirc: 'û',
    Eacute: 'É',
    Egrave: 'È',
    Agrave: 'À',
    oelig: 'œ',
    OElig: 'Œ',
    euro: '€',
    laquo: '«',
    raquo: '»',
    deg: '°',
    middot: '·',
    bull: '•',
    times: '×',
    copy: '©',
    reg: '®',
    trade: '™',
  };
  // Entités restantes (retour de Ségolène du 15/10 : « 15&euro; » dans la carte du cours, entité absente de la table) :
  // dans le navigateur, décodage complet par un <textarea> (texte brut, rien n'est exécuté) ; table ci-dessus hors DOM.
  const decode = (text: string): string => {
    if (typeof document !== 'undefined') {
      const area = document.createElement('textarea');
      area.innerHTML = text;
      return area.value;
    }
    return text
      .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
      .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
      .replace(/&([a-zA-Z]+);/g, (m, name) => named[name] ?? m);
  };
  return decode(
    html
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li)>/gi, '\n')
      .replace(/<[^>]*>/g, ''),
  )
    .replace(/[ \t\u00a0]+/g, ' ')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
}
